import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { buildCalibrationSeriesTail, buildResetOverrides } from '../../calibrationSeries';
import { algorithmVersionAtom } from '../../jotaiAtoms';

// Live inventory level for the period after the most recent calibrated datapoint.
// ConCal runs infrequently, so bin_set_calibration_series is usually stale; this
// hook fills the gap client-side using hourly feed summaries and the same ledger
// encoding as compute (continuous_calibration calibration_series.py).
// The tail is anchored to the last Calibrated Inventory Level point.

const CALIBRATION_TAIL_DATA_QUERY = gql`
  query CalibrationCard_TailData($bin_set_id: uuid!, $p_after: bigint!, $p_before: bigint!, $alg: String) {
    bin_set(where: { id: { _eq: $bin_set_id }, deleted_at: { _is_null: true } }) {
      id
      bins(where: { deleted_at: { _is_null: true } }) {
        capacity_in_grams
        deliveries(
          where: {
            deleted_at: { _is_null: true }
            status: { _eq: "delivered" }
            delivered_at: { _gt: $p_after }
          }
          order_by: { delivered_at: asc }
        ) {
          delivered_at
          weight_in_grams
        }
      }
      feed_lines(where: { deleted_at: { _is_null: true } }) {
        id
        feed_frame_summaries_v2(
          args: { p_after: $p_after, p_before: $p_before, p_interval: "hour", p_time_zone: "UTC", p_alg: $alg }
        ) {
          start_timestamp
          end_timestamp
          total_consumption
        }
      }
    }
  }
`;

export default function useCalibrationSeriesTail({
  binSetID,
  cachedCalibratedDataPoints = [],
  binSetLevels = [],
  now,
}) {
  const algorithmVersion = useAtomValue(algorithmVersionAtom);

  const lastCachedT = cachedCalibratedDataPoints.length
    ? [...cachedCalibratedDataPoints].sort((a, b) => a.t - b.t).at(-1)?.t
    : null;

  const fallbackAnchorTs = useMemo(() => {
    if (lastCachedT != null) {
      return null;
    }
    return buildResetOverrides(binSetLevels)[0]?.occurredAt ?? null;
  }, [lastCachedT, binSetLevels]);

  const anchorTs = lastCachedT ?? fallbackAnchorTs;

  const { loading, error, data } = useQuery(CALIBRATION_TAIL_DATA_QUERY, {
    variables: {
      bin_set_id: binSetID,
      p_after: anchorTs ?? 0,
      p_before: now?.unix(),
      alg: algorithmVersion || null,
    },
    skip: !binSetID || anchorTs == null || !now,
    fetchPolicy: 'no-cache',
  });

  const tailOriginalDataPoints = useMemo(() => {
    if (anchorTs == null || !data?.bin_set?.[0]) {
      return [];
    }

    const binSet = data.bin_set[0];

    const deliveries = [];
    binSet.bins?.forEach((bin) => {
      bin.deliveries?.forEach((delivery) => {
        deliveries.push({
          occurredAt: delivery.delivered_at,
          value: delivery.weight_in_grams,
        });
      });
    });

    const binSetCapacityInGrams =
      binSet.bins?.reduce((sum, bin) => sum + (bin.capacity_in_grams ?? 0), 0) ?? 0;

    return buildCalibrationSeriesTail({
      cachedCalibratedDataPoints,
      binSetLevels,
      feedLines: binSet.feed_lines ?? [],
      deliveries,
      binSetCapacityInGrams,
      nowTs: now.unix(),
    });
  }, [data, anchorTs, cachedCalibratedDataPoints, binSetLevels, now]);

  return { tailOriginalDataPoints, loading, error };
}
